import { useEffect, useRef, useState } from 'react';
import { buildWsUrl } from '@/lib/api';
import type { LogLine } from './parser';
import { parseLogLine } from './parser';

/**
 * 共享 raw ring buffer 上限。曾短暂涨到 200_000 想给"长跑挂着定位 lineId"
 * 多留余量，但叠加 wrap 模式（自动换行直接 `lines.map` 全量渲染，无虚拟
 * 化）会让一次点击触发数十万 DOM measure，浏览器直接卡死。
 *
 * 回退到 10_000 + 配合"点击锁定行自动冻结"的策略：
 *   - 正常滚动场景下 10_000 行已经能覆盖典型问题排查窗口；
 *   - 点击行 → 锁定 + 冻结流，缓冲不再增长，wrap 再大也不会因 evict
 *     重测量；要看更早的日志直接走"保存日志 / 离线导入"通路。
 */
const RING_SIZE = 10_000;

/**
 * Subscribers that want the *delta* (new parsed lines) of every chunk —
 * used by per-tab filtered ring buffers so they can survive even when the
 * shared 10_000-line raw ring evicts matched lines under heavy traffic.
 *
 * `cleared = true` is sent once when the user clicks "clear buffer", so
 * downstream consumers can reset their own buffers.
 */
export type ChunkListener = (chunk: LogLine[], cleared: boolean) => void;

interface SerialBundle {
  lines: LogLine[];
  /** Monotonically increasing version, bumped on each batch. */
  version: number;
  ws: WebSocket | null;
  refs: number;
  reconnectTimer?: ReturnType<typeof setTimeout>;
  /** Soft pause: when set, incoming frames are dropped (still keep socket open). */
  paused: boolean;
  /** Cumulative count of lines we've seen since the last clear. */
  totalSeen: number;
  /** Last time the stream produced new bytes. */
  lastActivity: number;
  /** Coarse-grained re-render notifier (status / version bumps). */
  listeners: Set<() => void>;
  /** Per-chunk listeners that receive newly-parsed lines. */
  chunkListeners: Set<ChunkListener>;
  /** Status flag for UI: connecting / open / closed / error. */
  status: 'connecting' | 'open' | 'closed' | 'error';
  statusMessage?: string;
  /** Set when a rAF-coalesced notify is already pending. */
  notifyPending: boolean;
}

const BUNDLES = new Map<string, SerialBundle>();

function bundleFor(serial: string): SerialBundle {
  let b = BUNDLES.get(serial);
  if (!b) {
    b = {
      lines: [],
      version: 0,
      ws: null,
      refs: 0,
      paused: false,
      totalSeen: 0,
      lastActivity: 0,
      listeners: new Set(),
      chunkListeners: new Set(),
      status: 'connecting',
      notifyPending: false,
    };
    BUNDLES.set(serial, b);
  }
  return b;
}

/**
 * Coarse UI tick used to batch React re-renders. At 60Hz rAF the visual
 * refresh exceeded what the human eye can read for any non-trivial log
 * stream (>30 行/s) and made tail-following visibly chase its target.
 * 100ms (10Hz) is what AS / DDMS use for their log viewer; the user still
 * perceives "live" while the bottom of the buffer stays optically stable
 * even at 1000+ 行/s. Tune at one place if needed.
 */
const UI_TICK_MS = 100;

/**
 * Throttled re-render notify. All `notify` calls inside the same
 * UI_TICK_MS window collapse into a single React commit. Per-chunk
 * subscribers (`emitChunk`) still receive every WS frame in real-time so
 * filter-side ring buffers / pause-backlog / event detection don't drop
 * data — the throttle is **only** for the visible render loop.
 */
function notify(b: SerialBundle): void {
  b.version++;
  if (b.notifyPending) return;
  b.notifyPending = true;
  setTimeout(() => {
    b.notifyPending = false;
    for (const l of b.listeners) l();
  }, UI_TICK_MS);
}

function emitChunk(b: SerialBundle, chunk: LogLine[], cleared: boolean): void {
  if (chunk.length === 0 && !cleared) return;
  for (const cb of b.chunkListeners) {
    try {
      cb(chunk, cleared);
    } catch {
      /* never let one buggy subscriber break the others */
    }
  }
}

function appendChunk(b: SerialBundle, chunk: string): void {
  if (b.paused) return;
  const parts = chunk.split(/\r?\n/);
  // The first part may complete a previous incomplete line; keep simple here
  // — chunk boundaries from logcat are usually whole-line, so we tolerate the
  // small risk of a torn line in exchange for code simplicity.
  const parsed: LogLine[] = [];
  for (const p of parts) {
    if (p.length === 0) continue;
    const ln = parseLogLine(p);
    // Phase 14：按全局序号给每行打上稳定 id，供 RunnerSet 的 lineId 复用。
    ln.id = b.totalSeen;
    parsed.push(ln);
    b.lines.push(ln);
    b.totalSeen++;
  }
  if (b.lines.length > RING_SIZE) {
    b.lines.splice(0, b.lines.length - RING_SIZE);
  }
  b.lastActivity = Date.now();
  notify(b);
  emitChunk(b, parsed, false);
}

function connect(serial: string, b: SerialBundle): void {
  if (b.ws) return;
  b.status = 'connecting';
  b.statusMessage = undefined;
  notify(b);
  const url = buildWsUrl('/api/logcat/stream', { serial });
  let ws: WebSocket;
  try {
    ws = new WebSocket(url);
  } catch (e) {
    b.status = 'error';
    b.statusMessage = e instanceof Error ? e.message : String(e);
    notify(b);
    scheduleReconnect(serial, b, 5_000);
    return;
  }
  b.ws = ws;
  ws.onopen = () => {
    b.status = 'open';
    b.statusMessage = undefined;
    notify(b);
  };
  ws.onmessage = (ev) => {
    appendChunk(b, String(ev.data));
  };
  ws.onerror = () => {
    b.status = 'error';
    b.statusMessage = 'websocket error';
    notify(b);
  };
  ws.onclose = (ev) => {
    b.ws = null;
    b.status = 'closed';
    b.statusMessage = ev.reason || `code ${ev.code}`;
    notify(b);
    // Auto-reconnect while subscribers still want this serial.
    if (b.refs > 0) scheduleReconnect(serial, b, 1_500);
  };
}

function scheduleReconnect(serial: string, b: SerialBundle, delayMs: number): void {
  if (b.reconnectTimer) clearTimeout(b.reconnectTimer);
  b.reconnectTimer = setTimeout(() => {
    if (b.refs > 0) connect(serial, b);
  }, delayMs);
}

function disconnect(b: SerialBundle): void {
  if (b.reconnectTimer) clearTimeout(b.reconnectTimer);
  b.reconnectTimer = undefined;
  if (b.ws) {
    try {
      b.ws.close(1000, 'no subscribers');
    } catch {
      /* ignore */
    }
    b.ws = null;
  }
}

/* -------------------------------------------------------------------- */
/* Public hook                                                          */
/* -------------------------------------------------------------------- */

export interface LogcatStream {
  lines: LogLine[];
  version: number;
  status: 'connecting' | 'open' | 'closed' | 'error';
  statusMessage: string | undefined;
  totalSeen: number;
  paused: boolean;
  setPaused: (v: boolean) => void;
  clear: () => void;
}

/**
 * Subscribe to the shared logcat stream for `serial`. Multiple subscribers
 * (e.g. one per logcat tab) share a single WebSocket and a single ring
 * buffer; the buffer is the source of truth and individual tabs apply their
 * own filtering on top.
 */
export function useLogcatStream(serial: string | undefined): LogcatStream {
  const [, setTick] = useState(0);
  const bRef = useRef<SerialBundle | null>(null);

  useEffect(() => {
    if (!serial) {
      bRef.current = null;
      return;
    }
    const b = bundleFor(serial);
    bRef.current = b;
    b.refs++;
    const listener = () => setTick((t) => t + 1);
    b.listeners.add(listener);
    if (!b.ws) connect(serial, b);
    setTick((t) => t + 1);
    return () => {
      b.refs = Math.max(0, b.refs - 1);
      b.listeners.delete(listener);
      if (b.refs === 0) {
        disconnect(b);
      }
    };
  }, [serial]);

  const b = bRef.current;
  return {
    lines: b?.lines ?? [],
    version: b?.version ?? 0,
    status: b?.status ?? 'closed',
    statusMessage: b?.statusMessage,
    totalSeen: b?.totalSeen ?? 0,
    paused: b?.paused ?? false,
    setPaused: (v: boolean) => {
      if (!b) return;
      b.paused = v;
      notify(b);
    },
    clear: () => {
      if (!b) return;
      b.lines = [];
      b.totalSeen = 0;
      notify(b);
      emitChunk(b, [], true);
    },
  };
}

/**
 * Subscribe to delta chunks for `serial`. The callback is invoked with the
 * newly-parsed lines whenever the upstream WS pushes a frame, and once with
 * `cleared=true` when the shared buffer is cleared. Use this to maintain a
 * private ring buffer (e.g. for per-tab filter results) that is independent
 * of the shared raw-line ring.
 *
 * The callback identity may change between renders; we always invoke the
 * latest one via a ref so the subscription itself only re-binds on serial
 * change.
 */
export function useLogcatChunks(
  serial: string | undefined,
  onChunk: ChunkListener
): void {
  const cbRef = useRef(onChunk);
  cbRef.current = onChunk;
  useEffect(() => {
    if (!serial) return;
    const b = bundleFor(serial);
    const handler: ChunkListener = (chunk, cleared) => {
      cbRef.current(chunk, cleared);
    };
    b.chunkListeners.add(handler);
    return () => {
      b.chunkListeners.delete(handler);
    };
  }, [serial]);
}
